import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  tmsApi,
  type CreateCustomerAdditionalChargeRequest,
  type CreateCustomerFuelSurchargeRequest,
  type CreateCustomerRateSheetRequest,
  type CreateCustomerRouteSheetRequest,
  type CustomerRateCard,
  type RateCardItemKindValue,
  type UpdateCustomerFuelSurchargeRequest,
  type UpdateCustomerRateSheetRequest,
  type UpdateCustomerRouteSheetRequest,
} from '../client';

/**
 * Query-key factory for the per-customer rate card. All four grids live under
 * one key, so any create/delete invalidates the whole card in one go.
 */
export const customerRateCardKeys = {
  all: ['tms', 'customer-rate-card'] as const,
  byCustomer: (organisationId: string, customerPartyId: string) =>
    ['tms', 'customer-rate-card', organisationId, customerPartyId] as const,
};

/**
 * Fetch a customer's full rate card. Disabled until both ids resolve (the org
 * comes from the Auth0 claim, the party from the route).
 */
export function useCustomerRateCard(
  organisationId: string | null,
  customerPartyId: string | null,
  options?: Omit<UseQueryOptions<CustomerRateCard, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<CustomerRateCard, Error>({
    queryKey:
      organisationId && customerPartyId
        ? customerRateCardKeys.byCustomer(organisationId, customerPartyId)
        : customerRateCardKeys.all,
    queryFn: () => {
      if (!organisationId || !customerPartyId) throw new Error('No organisation / customer id');
      return tmsApi.getCustomerRateCard(organisationId, customerPartyId);
    },
    enabled: !!organisationId && !!customerPartyId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

function useInvalidatingCreate<TReq extends { organisationId: string; customerPartyId: string }>(
  fn: (body: TReq) => Promise<string>,
) {
  const qc = useQueryClient();
  return useMutation<string, Error, TReq>({
    mutationFn: fn,
    onSuccess: (_id, vars) =>
      qc.invalidateQueries({
        queryKey: customerRateCardKeys.byCustomer(vars.organisationId, vars.customerPartyId),
      }),
  });
}

export function useCreateCustomerRateSheet() {
  return useInvalidatingCreate<CreateCustomerRateSheetRequest>(tmsApi.createCustomerRateSheet);
}
export function useCreateCustomerRouteSheet() {
  return useInvalidatingCreate<CreateCustomerRouteSheetRequest>(tmsApi.createCustomerRouteSheet);
}
export function useCreateCustomerFuelSurcharge() {
  return useInvalidatingCreate<CreateCustomerFuelSurchargeRequest>(tmsApi.createCustomerFuelSurcharge);
}
export function useCreateCustomerAdditionalCharge() {
  return useInvalidatingCreate<CreateCustomerAdditionalChargeRequest>(tmsApi.createCustomerAdditionalCharge);
}

/**
 * Shared shape for the in-place edits: the request only carries the item's own
 * id, so the org + party ids ride alongside it purely to invalidate the right
 * card on success.
 */
function useInvalidatingUpdate<TReq>(fn: (body: TReq) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { request: TReq; organisationId: string; customerPartyId: string }
  >({
    mutationFn: ({ request }) => fn(request),
    onSuccess: (_res, vars) =>
      qc.invalidateQueries({
        queryKey: customerRateCardKeys.byCustomer(vars.organisationId, vars.customerPartyId),
      }),
  });
}

/** Edit a rate sheet — a full replace of the sheet, its lines and its bands. */
export function useUpdateCustomerRateSheet() {
  return useInvalidatingUpdate<UpdateCustomerRateSheetRequest>(tmsApi.updateCustomerRateSheet);
}

/** Edit a route sheet — a full replace of the lane, its window and its lines. */
export function useUpdateCustomerRouteSheet() {
  return useInvalidatingUpdate<UpdateCustomerRouteSheetRequest>(tmsApi.updateCustomerRouteSheet);
}

/**
 * Update an existing fuel surcharge in place. The org + party ids ride along
 * (the request only carries the surcharge id) so the success handler can
 * invalidate the right card.
 */
export function useUpdateCustomerFuelSurcharge() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { request: UpdateCustomerFuelSurchargeRequest; organisationId: string; customerPartyId: string }
  >({
    mutationFn: ({ request }) => tmsApi.updateCustomerFuelSurcharge(request),
    onSuccess: (_res, vars) =>
      qc.invalidateQueries({
        queryKey: customerRateCardKeys.byCustomer(vars.organisationId, vars.customerPartyId),
      }),
  });
}

/**
 * Delete one rate-card item. The org + party ids ride along (not in the URL)
 * purely so the success handler can invalidate the right card.
 */
export function useDeleteCustomerRateCardItem() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { kind: RateCardItemKindValue; id: string; organisationId: string; customerPartyId: string }
  >({
    mutationFn: ({ kind, id }) => tmsApi.deleteCustomerRateCardItem(kind, id),
    onSuccess: (_res, vars) =>
      qc.invalidateQueries({
        queryKey: customerRateCardKeys.byCustomer(vars.organisationId, vars.customerPartyId),
      }),
  });
}
